/**
 * Adapts `@willnq/shared/design-system` (the cross-platform token source — see
 * src/shared/design-system, shared with `willnq-mobile`) into the shapes MUI's `createTheme`
 * expects: `rem` strings for font sizes, a CSS font stack, `box-shadow` strings, etc. Do not
 * hardcode a color/spacing/radius value here that isn't already a shared token
 * (docs/development/FrontendGuidelines.md §7.3/§7.7/§13.8) — add it to the shared design-system first so mobile
 * gets it too.
 */
import {
  brandColors,
  brandGradients,
  breakpointsMui as sharedBreakpoints,
  duration as sharedDuration,
  easing as sharedEasing,
  fontFamily,
  fontWeights as sharedFontWeights,
  radii as sharedRadii,
  statusColors,
  surfaceColors,
  typeScale,
} from '@willnq/shared/design-system';



export const radii = sharedRadii;

function toRem(px: number): string {
  return `${px / 16}rem`;
}

export const typography = {
  fontFamily: fontFamily.fallbackWeb,
  fontWeights: {
    regular: sharedFontWeights.regular,
    medium: sharedFontWeights.medium,
    semibold: sharedFontWeights.semibold,
    bold: sharedFontWeights.bold,
  },
  scale: {
    h1: {
      fontSize: toRem(typeScale.h1.fontSize),
      lineHeight: typeScale.h1.lineHeight,
      letterSpacing: `${typeScale.h1.letterSpacing}em`,
    },
    h2: {
      fontSize: toRem(typeScale.h2.fontSize),
      lineHeight: typeScale.h2.lineHeight,
      letterSpacing: `${typeScale.h2.letterSpacing}em`,
    },
    h3: {
      fontSize: toRem(typeScale.h3.fontSize),
      lineHeight: typeScale.h3.lineHeight,
      letterSpacing: `${typeScale.h3.letterSpacing}em`,
    },
    h4: {
      fontSize: toRem(typeScale.h4.fontSize),
      lineHeight: typeScale.h4.lineHeight,
      letterSpacing: `${typeScale.h4.letterSpacing}em`,
    },
    h5: {
      fontSize: toRem(typeScale.h5.fontSize),
      lineHeight: typeScale.h5.lineHeight,
      letterSpacing: `${typeScale.h5.letterSpacing}em`,
    },
    h6: {
      fontSize: toRem(typeScale.h6.fontSize),
      lineHeight: typeScale.h6.lineHeight,
      letterSpacing: `${typeScale.h6.letterSpacing}em`,
    },
    body1: { fontSize: toRem(typeScale.body1.fontSize), lineHeight: typeScale.body1.lineHeight },
    body2: { fontSize: toRem(typeScale.body2.fontSize), lineHeight: typeScale.body2.lineHeight },
    caption: {
      fontSize: toRem(typeScale.caption.fontSize),
      lineHeight: typeScale.caption.lineHeight,
      letterSpacing: `${typeScale.caption.letterSpacing}em`,
    },
    overline: {
      fontSize: toRem(typeScale.overline.fontSize),
      lineHeight: typeScale.overline.lineHeight,
      letterSpacing: `${typeScale.overline.letterSpacing}em`,
      textTransform: 'uppercase' as const,
    },
  },
};

export const brandPalette = brandColors;

/**
 * Semantic status colors — shared across every status pill in the product (application status,
 * roster status, vendor bench status) so users learn one color language, not several
 * (docs/development/FrontendGuidelines.md §7.9 RosterTable convention).
 */
export const statusPalette = statusColors;

/** Per-mode surface tokens (background / surface / elevated / border / text). */
export const surfaces = surfaceColors;

/** Signature brand gradients as ready-to-use CSS strings. */
export const gradients = {
  primary: brandGradients.primary.css,
  brand: brandGradients.brand.css,
  accent: brandGradients.accent.css,
  subtleLight: brandGradients.subtleLight.css,
  subtleDark: brandGradients.subtleDark.css,
} as const;

/**
 * Premium two-layer (ambient + key) `box-shadow` strings per mode. Light mode tints toward slate;
 * dark mode uses deeper blacks since colored shadows read poorly on a dark surface. Keyed to the
 * semantic elevation vocabulary so a component asks for `shadows.card`, never a raw value.
 */
export const webShadows = {
  light: {
    none: 'none',
    xs: '0 1px 2px rgba(15, 23, 42, 0.04), 0 1px 3px rgba(15, 23, 42, 0.06)',
    card: '0 2px 8px rgba(15, 23, 42, 0.06), 0 1px 2px rgba(15, 23, 42, 0.04)',
    raised: '0 10px 28px rgba(15, 23, 42, 0.12), 0 2px 6px rgba(15, 23, 42, 0.06)',
    menu: '0 6px 16px rgba(15, 23, 42, 0.08), 0 2px 6px rgba(15, 23, 42, 0.05)',
    dialog: '0 24px 48px rgba(15, 23, 42, 0.18), 0 8px 16px rgba(15, 23, 42, 0.08)',
    focusPrimary: '0 0 0 3px rgba(124, 58, 237, 0.18)',
    focusError: '0 0 0 3px rgba(244, 63, 94, 0.18)',
  },
  dark: {
    none: 'none',
    xs: '0 1px 2px rgba(0, 0, 0, 0.30)',
    card: '0 2px 8px rgba(0, 0, 0, 0.36), 0 1px 2px rgba(0, 0, 0, 0.24)',
    raised: '0 12px 28px rgba(0, 0, 0, 0.50), 0 2px 6px rgba(0, 0, 0, 0.30)',
    menu: '0 6px 16px rgba(0, 0, 0, 0.44)',
    dialog: '0 24px 48px rgba(0, 0, 0, 0.60), 0 8px 16px rgba(0, 0, 0, 0.40)',
    focusPrimary: '0 0 0 3px rgba(167, 139, 250, 0.24)',
    focusError: '0 0 0 3px rgba(251, 113, 133, 0.24)',
  },
} as const;

/** Motion primitives as CSS-ready strings (durations in ms, easings as cubic-bezier). */
export const motion = {
  duration: sharedDuration,
  easing: {
    standard: `cubic-bezier(${sharedEasing.standard.join(', ')})`,
    decelerate: `cubic-bezier(${sharedEasing.decelerate.join(', ')})`,
    accelerate: `cubic-bezier(${sharedEasing.accelerate.join(', ')})`,
    emphasized: `cubic-bezier(${sharedEasing.emphasized.join(', ')})`,
    spring: `cubic-bezier(${sharedEasing.spring.join(', ')})`,
  },
  /** The default transition applied to interactive surfaces (buttons, cards, rows). */
  hover: `all ${sharedDuration.short}ms cubic-bezier(${sharedEasing.standard.join(', ')})`,
} as const;

export const breakpoints = {
  // Candidate persona is mobile-responsive down to 360px (§7.5); workstation personas
  // (Recruiter/Company/Vendor/Admin) target desktop first.
  values: sharedBreakpoints,
};
